import { useCallback, useEffect, useRef, useState } from "react";
import { MessageThread, MessageThreadsSearchResult } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import { Subscription } from "apollo-client/util/Observable";
import { debounce } from "lodash-es";
import useAuthUser from "hooks/useAuthUser";
import emitter, { EVENTS } from "lib/emitter";
import { cacheGroup, cacheThreads, getCachedThreads } from "lib/offlineCache";
import providers from "lib/providers";

function useController() {
  const [threads, setThreads] = useState<MessageThreadsSearchResult>();
  // Starts true so the first paint never flashes the empty-state ("No conversations
  // yet") before the cache/network load resolves — there is no spinner anymore, so
  // this flag now only gates the empty-state. Cleared once the background fetch lands.
  const [loadingThreads, setLoadingThreads] = useState<boolean>(true);
  const [personalPage, setPersonalPage] = useState(0);
  const [groupPage, setGroupPage] = useState(0);
  const [personalHasNext, setPersonalHasNext] = useState(false);
  const [groupHasNext, setGroupHasNext] = useState(false);
  const [hiddenThreads, setHiddenThreads] = useState<MessageThread[]>([]);
  const [showHidden, setShowHidden] = useState(false);
  const [loadingHidden, setLoadingHidden] = useState(false);

  const { user } = useAuthUser();

  const getMessageThreads = useCallback(
    async (pPage = 0, gPage = 0, replace = true, skipPersonal = false, skipGroup = false) => {
      let personalResponse: any;
      let groupResponse: any;
      try {
        [personalResponse, groupResponse] = await Promise.all([
          skipPersonal
            ? Promise.resolve({ result: [], hasNextPage: false } as any)
            : providers.MessageThreadsProvider.find({
                data: { userId: user?.id || "", sort: "UPDATED_TIME,DESC", page: pPage, size: 20, hidden: false },
              }),
          skipGroup
            ? Promise.resolve({ result: [], hasNextPage: false } as any)
            : providers.MessageThreadsProvider.find({
                data: {
                  userId: user?.id || "",
                  subjectType: "group",
                  sort: "UPDATED_TIME,DESC",
                  page: gPage,
                  size: 20,
                  hidden: false,
                },
              }),
        ]);
      } catch (e) {
        // Offline fallback: serve cached threads on first page load
        if (!navigator.onLine && pPage === 0 && gPage === 0) {
          const cached = await getCachedThreads();
          if (cached.length > 0) {
            setThreads({ result: cached, hasNextPage: false } as any);
            return;
          }
        }
        throw e;
      }

      setPersonalPage(pPage);
      setGroupPage(gPage);
      if (!skipPersonal) setPersonalHasNext(personalResponse.hasNextPage);
      if (!skipGroup) setGroupHasNext(groupResponse.hasNextPage);

      // Resolve group metadata for group threads
      const groupThreads = groupResponse.result.filter(
        (t: any) => t.subject?.subjectType === "group" && t.subject?.subjectId
      );
      const groupInfoMap = new Map<string, any>();
      if (groupThreads.length > 0) {
        await Promise.all(
          groupThreads.map(async (t: any) => {
            try {
              const groups = await providers.GroupsProvider.search({ data: { id: t.subject.subjectId } });
              if (groups.length > 0) groupInfoMap.set(String(t.subject.subjectId), groups[0]);
            } catch {
              /* ignore */
            }
          })
        );
        groupThreads.forEach((t: any) => {
          const g = groupInfoMap.get(String(t.subject.subjectId));
          if (!g) return;
          t.groupName = g.name;
          t.groupSlug = g.slug;
          t.groupTopicsEnabled = g.topicsEnabled;
          // Prime the group cache: GroupMessagesList.hydrate() then knows
          // `topicsEnabled` on the FIRST open and renders the Topics overview
          // directly — no main-chat flash. Keyed by both URL token variants
          // (slug and numeric id).
          cacheGroup(String(g.slug || t.subject.subjectId), g, t).catch(() => {});
          if (g.slug) cacheGroup(String(t.subject.subjectId), g, t).catch(() => {});
        });
      }

      const newPersonal = personalResponse.result;
      const newGroup = groupResponse.result;

      setThreads((prevState) => {
        let allPersonal = newPersonal;
        let allGroup = newGroup;

        if (!replace && prevState?.result) {
          const existingIds = new Set<string>();
          const existingPersonal: any[] = [];
          const existingGroup: any[] = [];
          prevState.result.forEach((t: any) => {
            existingIds.add(String(t.id));
            if (t.subject?.subjectType === "group") {
              existingGroup.push(t);
            } else {
              existingPersonal.push(t);
            }
          });
          allPersonal = [...existingPersonal, ...newPersonal.filter((t: any) => !existingIds.has(String(t.id)))];
          allGroup = [...existingGroup, ...newGroup.filter((t: any) => !existingIds.has(String(t.id)))];
        }

        const merged = [...allPersonal, ...allGroup].sort((a: any, b: any) => {
          const timeA = a.lastMessage?.time || a.updatedTime || a.createdTime || "";
          const timeB = b.lastMessage?.time || b.updatedTime || b.createdTime || "";
          return timeB.localeCompare(timeA);
        });

        return {
          ...personalResponse,
          result: merged,
          hasNextPage: personalResponse.hasNextPage || groupResponse.hasNextPage,
        };
      });

      // Cache threads for offline access (outside setState to keep updater pure)
      const allThreads = [...newPersonal, ...newGroup];
      cacheThreads(allThreads).catch(() => {});
    },
    [user?.id]
  );

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    // Hydrate instantly from the IndexedDB cache so returning users see the final
    // list immediately — no loading spinner, no empty-state flash (loadingThreads
    // starts true). The background fetch then refreshes with live data and clears
    // the loading flag. A genuine cold start (empty cache) renders nothing — not a
    // spinner — until the list arrives (see MessageThreadList/index.tsx).
    getCachedThreads()
      .then((cached) => {
        if (cancelled || cached.length === 0) return;
        // Never clobber a fresh response that may have already landed.
        setThreads((prev) => (prev?.result?.length ? prev : ({ result: cached, hasNextPage: false } as any)));
      })
      .catch(() => {
        /* no IndexedDB (SSR/jsdom) — fall through to network */
      })
      .finally(() => {
        if (cancelled) return;
        getMessageThreads().finally(() => {
          if (!cancelled) setLoadingThreads(false);
        });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = useCallback(
    async (cb?: () => void) => {
      if (!personalHasNext && !groupHasNext) return;
      return await getMessageThreads(
        personalHasNext ? personalPage + 1 : personalPage,
        groupHasNext ? groupPage + 1 : groupPage,
        false,
        !personalHasNext,
        !groupHasNext
      ).finally(() => cb && cb());
    },
    [personalHasNext, groupHasNext, personalPage, groupPage, getMessageThreads]
  );

  const getMessageThreadsRef = useRef(getMessageThreads);
  getMessageThreadsRef.current = getMessageThreads;

  const showHiddenRef = useRef(false);
  showHiddenRef.current = showHidden;

  const loadHiddenThreadsRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    if (!user) return;
    // New message: debounce 300ms — fast response, batches rapid messages
    const onNewMessage = debounce(() => getMessageThreadsRef.current(), 300);
    // Mark read: immediate — badge must disappear instantly when user reads messages
    const onMarkedRead = () => getMessageThreadsRef.current();
    // Hidden list refresh on mark read
    const refreshHidden = debounce(() => {
      if (showHiddenRef.current && loadHiddenThreadsRef.current) loadHiddenThreadsRef.current();
    }, 1000);
    // User actions (hide/delete/leave): immediate, no delay
    const immediateRefresh = () => getMessageThreadsRef.current();

    emitter.on(EVENTS.MESSAGES_NEW_MESSAGE, onNewMessage);
    emitter.on(EVENTS.MESSAGES_MARKED_READ, onMarkedRead);
    emitter.on(EVENTS.MESSAGES_MARKED_READ, refreshHidden);
    emitter.on(EVENTS.THREADS_REFRESH, immediateRefresh);

    // Subscribe to real-time thread updates via WebSocket
    let threadSubscription: Subscription | null = null;
    let subscriptionCancelled = false;
    (async () => {
      try {
        const sub = await providers.MessageThreadsProvider.subscribeToThreadUpdated((updatedThread: MessageThread) => {
          // Group Topics (Темы): per-topic threads must never surface as
          // top-level chats — the group's General thread represents the group
          // in this list. (The initial fetch already excludes them server-side;
          // this guards the live-update path, which delivers topic-thread
          // updates because the member is in the thread's userIds.)
          if (updatedThread.topicId) return;
          setThreads((prev) => {
            if (!prev) return prev;
            const existingIdx = prev.result.findIndex((t) => String(t.id) === String(updatedThread.id));
            let newResult: MessageThread[];
            if (existingIdx >= 0) {
              // Update existing thread in-place
              newResult = prev.result.map((t) =>
                String(t.id) === String(updatedThread.id) ? { ...t, ...updatedThread } : t
              );
            } else {
              // New thread — add to list
              newResult = [updatedThread, ...prev.result];
            }
            // Re-sort by latest activity
            newResult.sort((a: any, b: any) => {
              const timeA = a.lastMessage?.time || a.updatedTime || a.createdTime || "";
              const timeB = b.lastMessage?.time || b.updatedTime || b.createdTime || "";
              return timeB.localeCompare(timeA);
            });
            return { ...prev, result: newResult };
          });
        });
        if (subscriptionCancelled) {
          sub.unsubscribe();
          return;
        }
        threadSubscription = sub;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[threads] WebSocket subscription failed, relying on polling:", e);
      }
    })();

    // Poll every 60s as fallback (WebSocket handles real-time updates)
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    const startPolling = () => {
      if (pollInterval) return;
      pollInterval = setInterval(() => {
        getMessageThreadsRef.current();
      }, 60000);
    };
    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        getMessageThreadsRef.current(); // Refresh immediately on tab focus
        startPolling();
      } else {
        stopPolling();
      }
    };
    startPolling();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      emitter.off(EVENTS.MESSAGES_NEW_MESSAGE, onNewMessage);
      emitter.off(EVENTS.MESSAGES_MARKED_READ, onMarkedRead);
      emitter.off(EVENTS.MESSAGES_MARKED_READ, refreshHidden);
      emitter.off(EVENTS.THREADS_REFRESH, immediateRefresh);
      onNewMessage.cancel();
      refreshHidden.cancel();
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibility);
      subscriptionCancelled = true;
      if (threadSubscription) threadSubscription.unsubscribe();
    };
  }, [user]);

  const refresh = useCallback(() => {
    return getMessageThreads();
  }, [getMessageThreads]);

  const loadHiddenThreads = useCallback(async () => {
    if (!user) return;
    setLoadingHidden(true);
    try {
      const [personalHidden, groupHidden] = await Promise.all([
        providers.MessageThreadsProvider.find({
          data: { userId: user.id, hidden: true, sort: "UPDATED_TIME,DESC", size: 50 },
        }),
        providers.MessageThreadsProvider.find({
          data: { userId: user.id, subjectType: "group", hidden: true, sort: "UPDATED_TIME,DESC", size: 50 },
        }),
      ]);
      // Resolve group names for hidden group threads
      const groupThreads = groupHidden.result.filter(
        (t: any) => t.subject?.subjectType === "group" && t.subject?.subjectId
      );
      if (groupThreads.length > 0) {
        await Promise.all(
          groupThreads.map(async (t: any) => {
            try {
              const groups = await providers.GroupsProvider.search({ data: { id: t.subject.subjectId } });
              if (groups.length > 0) t._groupName = groups[0].name;
            } catch {
              /* ignore */
            }
          })
        );
      }
      const allHidden = [...personalHidden.result, ...groupHidden.result];
      const uniqueIds = new Set<string>();
      const deduped = allHidden.filter((t: any) => {
        if (uniqueIds.has(String(t.id))) return false;
        uniqueIds.add(String(t.id));
        return true;
      });
      setHiddenThreads(deduped);
    } catch {
      /* ignore */
    } finally {
      setLoadingHidden(false);
    }
  }, [user]);

  loadHiddenThreadsRef.current = loadHiddenThreads;

  const toggleShowHidden = useCallback(() => {
    setShowHidden((prev) => {
      const next = !prev;
      if (next) loadHiddenThreads();
      return next;
    });
  }, [loadHiddenThreads]);

  const restoreThread = useCallback(
    async (threadId: string) => {
      try {
        await providers.MessageThreadsProvider.hide({ id: threadId });
        setHiddenThreads((prev) => prev.filter((t) => String(t.id) !== String(threadId)));
        getMessageThreads();
      } catch {
        /* ignore */
      }
    },
    [getMessageThreads]
  );

  return {
    messageThreadList: threads?.result || [],
    scrollThreads: !loadingThreads && (threads?.hasNextPage ?? false),
    threads,
    loadingThreads,
    loadMore,
    refresh,
    user,
    hiddenThreads,
    showHidden,
    loadingHidden,
    toggleShowHidden,
    restoreThread,
    loadHiddenThreads,
  };
}

export default useController;
